<script setup>
import { ref, onMounted, defineProps, defineEmits } from 'vue';
import Data from '@/api/data';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  }
});

const emit = defineEmits(['update:modelValue']);

const PREFERRED_SOURCES = ['location', 'gpgga_Internal'];

const config = ref({
  source: 'gpgga_Internal',
  latitudeField: 'lat',
  longitudeField: 'lon',
  ...props.modelValue
});

const loadingData = ref(false);
const availableSources = ref([]);

const getSubcollections = async () => {
  loadingData.value = true;
  try {
    const response = await Data.get(props.dataID);
    if (response.body.status === 'success') {
      const allKeys = Object.keys(response.body.details);
      const preferred = PREFERRED_SOURCES.filter(s => allKeys.includes(s));
      availableSources.value = preferred.length > 0 ? preferred : allKeys.sort((a, b) => a.localeCompare(b));
    }
  } catch (err) {
    console.error('Error fetching subcollections:', err);
  } finally {
    loadingData.value = false;
  }
};

const handleSourceChange = (newSource) => {
  config.value.source = newSource;
  emitUpdate();
};

const emitUpdate = () => {
  emit('update:modelValue', config.value);
};

onMounted(async () => {
  await getSubcollections();

  // If the saved source is no longer in the list, default to the first available
  if (availableSources.value.length > 0 && !availableSources.value.includes(config.value.source)) {
    config.value.source = availableSources.value[0];
  }

  emitUpdate();
});
</script>

<template>
  <div class="space-y-4">
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Data Source *
        <span v-if="loadingData" class="inline-flex items-center ml-2" title="Loading options...">
          <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
        </span>
      </label>
      <select
        :value="config.source"
        @change="handleSourceChange($event.target.value)"
        :disabled="loadingData"
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">Select a source...</option>
        <option v-for="source in availableSources" :key="source" :value="source">
          {{ source }}
        </option>
      </select>
      <p v-if="!loadingData && availableSources.length === 0" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
        No location sources found for this device.
      </p>
    </div>
  </div>
</template>
